// String → structured tonal shape. Used by the scale & chord pickers
// to decide whether they can render their dropdown form or must
// fall back to raw text editing.

export const ROOTS = [
  'C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B',
] as const;

// Accepted alias → canonical. Strudel accepts a few alternative
// spellings; we display the canonical one the picker can round-trip.
export const ROOT_ALIASES: Record<string, string> = {
  Db: 'C#',
  'D#': 'Eb',
  Gb: 'F#',
  'G#': 'Ab',
  'A#': 'Bb',
  Cb: 'B',
  'E#': 'F',
  'B#': 'C',
};

export const SCALE_MODES = [
  'major',
  'minor',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
  'locrian',
  'harmonicMinor',
  'melodicMinor',
  'pentatonic',
  'blues',
  'chromatic',
] as const;

export const CHORD_QUALITIES = [
  '',
  'm',
  '7',
  'maj7',
  'm7',
  'dim',
  'aug',
  'sus2',
  'sus4',
  'add9',
  'm7b5',
  'dim7',
  '9',
  'm9',
  'maj9',
] as const;

export function canonicalRoot(name: string): string | null {
  if ((ROOTS as readonly string[]).includes(name)) return name;
  if (name in ROOT_ALIASES) return ROOT_ALIASES[name]!;
  return null;
}

// "C:major", "F# dorian", "Eb:minor" — returns null when the string
// doesn't match a root we recognise followed by a mode we know.
export function parseScaleString(
  s: string,
): { root: string; mode: string } | null {
  const match = s.trim().match(/^([A-G][#b]?)\s*[:\s]\s*(\w+)$/);
  if (!match) return null;
  const root = canonicalRoot(match[1]!);
  if (!root) return null;
  const mode = match[2]!;
  if (!(SCALE_MODES as readonly string[]).includes(mode)) return null;
  return { root, mode };
}

// "Cm7", "F#", "Abmaj7". Root is 1–2 chars, the rest is the quality.
export function parseChordString(
  s: string,
): { root: string; quality: string } | null {
  const trimmed = s.trim();
  const rootMatch = trimmed.match(/^[A-G][#b]?/);
  if (!rootMatch) return null;
  const root = canonicalRoot(rootMatch[0]);
  if (!root) return null;
  const quality = trimmed.slice(rootMatch[0].length);
  if (!(CHORD_QUALITIES as readonly string[]).includes(quality)) return null;
  return { root, quality };
}
